import { useDetectTooManyRedraws } from "@/hooks/use-detect-too-many-redraws";
import { selectIsPanoAligned } from "@/modes/walk-mode/walk-scene-selectors";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  atEyeLevel,
  selectPanoCameraTransform,
} from "@/utils/camera-transform";
import {
  findClosestIndex,
  selectChildrenDepthFirst,
} from "@faro-lotv/app-component-toolbox";
import {
  GUID,
  IElementGenericImgSheet,
  IElementImg360,
  isIElementImg360,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useState, type JSX } from "react";
import { Vector3, type Plane, type Quaternion } from "three";
import { WaypointPosition } from "../waypoint-label-render";
import { PathWaypointTarget } from "./odometry-path-renderer";
import { WalkPathRenderer } from "./walk-path-renderer";

/** Used to specify properties of the WayPoint target clicked by user in walk mode (on 360 images, cad and cloud) */
type WayPointTarget = {
  /** Target element */
  targetElement: IElementImg360;

  /** Position of the target (can be used to center "other" camera in split view) */
  position?: Vector3;

  /** Rotation of the camera (can be used to set rotation of "other" camera in split view) */
  quaternion?: Quaternion;
};

type WalkPathsRendererProps = {
  /** Whether to fade off the path in the distance. */
  fadeOff?: boolean;

  /** sheet whose elevation will be used to render trajectories for */
  sheetForElevation?: IElementGenericImgSheet;

  /** Paths to render */
  paths: IElementGenericOdometryPath[];

  /** Current active path */
  activePath?: IElementGenericOdometryPath;

  /** The currently active pano, if present. */
  activePano?: IElementImg360;

  /**
   * Check if the pano should be aligned in order to make the component visible
   *
   * @default false
   */
  alignmentRequired?: boolean;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** Element that should be highlighted, e.g. with a hover effect */
  highlightedElement?: GUID;

  /**
   * A callback for when the active element is being changed.
   *
   * @param el The new active element.
   */
  setActiveElement?(el: IElementImg360): void;

  /** Callback issued when a waypoint without a panorama is clicked, to walk to its position. */
  onWalkToPosition?(position: Vector3): void;

  /** Callback called when the active waypoint target element inside the walk scene has changed. */
  onWayPointChanged?(target: WayPointTarget): void;

  /** Callback called when the list of displayed waypoints has changed. The points are grouped according to the input paths. */
  onRenderedWaypointsChanged?(newWaypoints: WaypointPosition[][]): void;
};

/**
 * @returns A single odometric path for usage in 3D environments.
 */
export function WalkPathsRenderer({
  fadeOff,
  paths,
  activePano,
  activePath,
  sheetForElevation,
  alignmentRequired = false,
  clippingPlanes,
  highlightedElement,
  setActiveElement,
  onWalkToPosition,
  onWayPointChanged,
  onRenderedWaypointsChanged,
}: WalkPathsRendererProps): JSX.Element | null {
  useDetectTooManyRedraws("WalkPathsRenderer");

  const isAligned = useAppSelector(selectIsPanoAligned(activePano));

  const camera = useThree((s) => s.camera);

  const store = useAppStore();

  const notifyNewWayPoint = useCallback(
    (el: IElementImg360) => {
      if (onWayPointChanged) {
        const { position } = selectPanoCameraTransform(
          el,
          sheetForElevation,
        )(store.getState());
        onWayPointChanged({
          targetElement: el,
          position: new Vector3(...position),
          quaternion: camera.quaternion,
        });
      }
    },
    [sheetForElevation, camera.quaternion, onWayPointChanged, store],
  );

  // Select the closest pano in the path to the point clicked on the path
  const changeActivePath = useCallback(
    (
      odometricPath: IElementGenericOdometryPath,
      position: Vector3,
      placeholderPoints: Vector3[],
    ) => {
      const state = store.getState();
      const panos = selectChildrenDepthFirst(
        odometricPath,
        isIElementImg360,
      )(state);
      const closest = findClosestIndex(position, placeholderPoints);
      if (closest === undefined) return;
      const pano = panos.at(closest);
      if (pano) {
        setActiveElement?.(pano);
        notifyNewWayPoint(pano);
      } else {
        onWalkToPosition?.(
          atEyeLevel(
            state.iElements,
            odometricPath,
            placeholderPoints[closest],
          ),
        );
      }
    },
    [notifyNewWayPoint, onWalkToPosition, setActiveElement, store],
  );

  const onWaypointClicked = useCallback(
    ({ path, element, position }: PathWaypointTarget) => {
      if (element) {
        setActiveElement?.(element);
        notifyNewWayPoint(element);
      } else {
        onWalkToPosition?.(
          atEyeLevel(store.getState().iElements, path, position),
        );
      }
    },
    [notifyNewWayPoint, onWalkToPosition, setActiveElement, store],
  );

  const setWaypointsForPath = useCombinedWaypoints(
    paths,
    onRenderedWaypointsChanged,
  );

  if (!isAligned && alignmentRequired) {
    return null;
  }

  return (
    <>
      {paths.map((path) => (
        <WalkPathRenderer
          activePano={activePano}
          sheetForElevation={sheetForElevation}
          odometricPath={path}
          key={path.id}
          isActive={path.id === activePath?.id}
          onPathClicked={changeActivePath}
          onPlaceholderClicked={onWaypointClicked}
          fadeOff={fadeOff}
          clippingPlanes={clippingPlanes}
          highlightedElement={highlightedElement}
          onWaypointsChanged={setWaypointsForPath}
        />
      ))}
    </>
  );
}

/** A function to notify a change of waypoints for a specific path */
type SetWaypointsForPathFunction = (
  pathId: GUID,
  waypoints: WaypointPosition[],
) => void;

/**
 * Hook that keeps track of waypoints for multiple paths and notifies the parent component when they change.
 *
 * @param paths The paths for which waypoints are being tracked.
 * @param onRenderedWaypointsChanged A callback, that runs whenever the waypoints for a path change.
 * @returns a function to set the waypoints for a given path
 */
function useCombinedWaypoints(
  paths: IElementGenericOdometryPath[],
  onRenderedWaypointsChanged?: (newWaypoints: WaypointPosition[][]) => void,
): SetWaypointsForPathFunction {
  const [waypointsMap, setWaypointsMap] = useState<
    Record<GUID, WaypointPosition[]>
  >({});

  // Notify the parent component when waypoints change
  useEffect(() => {
    onRenderedWaypointsChanged?.(
      paths.map((path) => waypointsMap[path.id] ?? []),
    );
  }, [onRenderedWaypointsChanged, paths, waypointsMap]);

  // Clean up paths that are no longer rendered
  useEffect(() => {
    setWaypointsMap((prevMap) => {
      const newMap: Record<GUID, WaypointPosition[]> = {};

      for (const path of paths) {
        newMap[path.id] = prevMap[path.id];
      }

      return newMap;
    });
  }, [paths]);

  return useCallback((pathId: GUID, waypoints: WaypointPosition[]) => {
    setWaypointsMap((prevMap) => ({
      ...prevMap,
      [pathId]: waypoints,
    }));
  }, []);
}
